import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError, createAccountApi } from '../../api/client';
import type { EmailActionView, Envelope } from '../../api/contract';
import { captureAuthEpoch } from '../../app/auth-context';
import { EmailActionPage } from './EmailActionPage';
import { cancelLinkReauth, createAuthRequestKey, emailActionGet, isTerminalReauthFailure, REAUTH_ROOT, reauthFailureText, startLinkReauth, type AccountApi, type ReauthStatus } from './api';
import { clearReauthIntent, hasFreshReauth, ownsReauthIntent, readReauthIntent, reauthReturnPath, saveReauthIntent, SIGN_IN_METHODS, type ReauthIntent } from './reauthReturn';
import './auth.css';
import { EMAIL_LINK_LOGIN } from './yandexIntent';

async function checkAction(api: AccountApi, actionId: string): Promise<void> {
  const result = await emailActionGet<Envelope<EmailActionView>>(actionId, api.epoch);
  api.assertCurrent();
  if (result.data.actionId !== actionId || result.data.purpose !== 'LINK_EMAIL') throw new ApiError(409, 'EMAIL_ACTION_CONTEXT_CHANGED', 'В другой вкладке открыто другое письмо. Откройте исходное письмо заново.');
  if (Date.parse(result.data.expiresAt) <= Date.now()) throw new ApiError(410, 'EMAIL_ACTION_UNAVAILABLE', 'Срок действия письма истёк. Запросите новое письмо.');
}

// This public wrapper has no financial bootstrap. Session preparation happens
// only after a confirm/save click and uses the same immutable epoch as the form.
export function EmailLinkPage() {
  const [api] = useState(() => createAccountApi(captureAuthEpoch()));
  const [loginNeeded, setLoginNeeded] = useState(false);
  const challengeKey = useRef<string | undefined>(undefined);
  const ownedIntent = useRef<ReauthIntent | undefined>(undefined);
  const prepareLink = async ({ actionId, phase, replay }: { actionId: string; phase: 'confirm' | 'complete'; replay?: true }) => {
    if (!replay) {
      const intent = readReauthIntent();
      ownedIntent.current = intent?.epoch === api.epoch && intent.actionId === actionId ? intent : undefined;
    }
    try {
      await api.apiGet('/me'); // Existing protected flow refreshes expired ACCESS.
      api.assertCurrent();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) setLoginNeeded(true);
      throw caught;
    }
    // A retained ambiguous POST must reach the server's receipt check with its
    // original body/key. A consumed action cannot pass a new-action preflight.
    if (phase === 'confirm' || replay || hasFreshReauth(api.epoch, actionId)) return true;
    // Only a known absent/locally expired grant starts reauth. Generic server
    // 401/403 is never interpreted as expiry or retried as another account.
    await checkAction(api, actionId);
    challengeKey.current ??= createAuthRequestKey();
    const url = await startLinkReauth(api, challengeKey.current, actionId);
    api.assertCurrent(); window.location.assign(url);
    return false;
  };
  const completed = async () => {
    clearReauthIntent(ownedIntent.current);
    // No /me bootstrap or membership mutation: reread only verified identities.
    await api.apiGet('/me/identities');
  };
  const failed = (error: unknown) => { if (isTerminalReauthFailure(error)) clearReauthIntent(ownedIntent.current); };
  return <EmailActionPage prepareLink={prepareLink} onLinkComplete={completed} onLinkFailure={failed} linkRecovery={loginNeeded && <>
      <p>Войдите через Яндекс ID в тот же аккаунт, затем заново откройте исходную ссылку из письма.</p>
      <Link className="button button-primary" to={EMAIL_LINK_LOGIN}>Войти через Яндекс ID</Link>
    </>} />;
}

export function YandexReauthPage() {
  const navigate = useNavigate();
  const [intent] = useState(readReauthIntent);
  const [api] = useState(() => createAccountApi(intent?.epoch ?? captureAuthEpoch()));
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);
  const [terminal, setTerminal] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [retryAt, setRetryAt] = useState(0);
  const busy = useRef(false);
  const active = useRef(false);
  const exchangeKey = useRef<string | undefined>(undefined);
  const cancelKey = useRef<string | undefined>(undefined);
  const deadline = useRef(intent?.grantUntil);
  const callbackAction = new URLSearchParams(window.location.search).get('actionId') ?? undefined;
  const valid = Boolean(intent && intent.actionId === callbackAction);
  const assertOwnedIntent = () => {
    if (!ownsReauthIntent(intent)) throw new ApiError(409, 'EMAIL_ACTION_CONTEXT_CHANGED', 'Контекст подтверждения изменился.');
  };
  useEffect(() => { active.current = true; document.title = 'Подтверждение входа — Казна'; return () => { active.current = false; }; }, []);
  useEffect(() => {
    if (!retryAt) return;
    const timer = window.setTimeout(() => setRetryAt(0), Math.min(Math.max(0, retryAt - Date.now()), 2_147_483_647));
    return () => window.clearTimeout(timer);
  }, [retryAt]);

  const finish = async (cancel = false) => {
    if (!intent || !valid || busy.current || terminal || retryAt > Date.now()) return;
    busy.current = true; setPending(true); setError('');
    try {
      await api.apiGet('/me');
      api.assertCurrent();
      assertOwnedIntent();
      if (cancel) {
        cancelKey.current ??= createAuthRequestKey();
        await cancelLinkReauth(api, intent.challengeId, cancelKey.current);
        clearReauthIntent(intent);
        if (active.current) { setTerminal(true); setError('Подтверждение отменено. Вернитесь к настройкам или исходному письму.'); }
        return;
      }
      if (intent.actionId) await checkAction(api, intent.actionId);
      if (!hasFreshReauth(api.epoch, intent.actionId)) {
        if (!exchangeKey.current) {
          const result = await api.apiGet<Envelope<ReauthStatus>>(`${REAUTH_ROOT}/${intent.challengeId}`);
          if (result.data.challengeId !== intent.challengeId) throw new Error('Контекст подтверждения изменился.');
          if (result.data.status === 'PENDING') { if (active.current) setWaiting(true); return; }
          if (result.data.status !== 'CONFIRMED') { clearReauthIntent(intent); if (active.current) { setTerminal(true); setError('Подтверждение отклонено или истекло. Вернитесь к настройкам или исходному письму.'); } return; }
        }
        exchangeKey.current ??= createAuthRequestKey();
        // Capture BEFORE the first request. Same-key retries never extend this
        // advisory bound, even if the original server response was lost.
        deadline.current ??= Date.now() + 5 * 60_000;
        assertOwnedIntent();
        saveReauthIntent({ ...intent, grantUntil: deadline.current, ready: false });
        await api.authMutation(`${REAUTH_ROOT}/${intent.challengeId}/exchange`, undefined, exchangeKey.current);
        api.assertCurrent();
        assertOwnedIntent();
        saveReauthIntent({ ...intent, grantUntil: deadline.current, ready: true });
      }
      if (intent.actionId) await checkAction(api, intent.actionId);
      api.assertCurrent();
      if (active.current) navigate(reauthReturnPath(intent.actionId), { replace: true });
    } catch (caught) {
      if (isTerminalReauthFailure(caught)) clearReauthIntent(intent);
      if (active.current) {
        setError(reauthFailureText(caught));
        if (caught instanceof ApiError) {
          if (isTerminalReauthFailure(caught)) setTerminal(true);
          if (caught.retryAfterMs !== undefined) setRetryAt(Date.now() + caught.retryAfterMs);
        }
      }
    } finally { busy.current = false; if (active.current) setPending(false); }
  };
  return <main className="login-page public-auth-page email-action-page"><section className="login-card" aria-labelledby="reauth-title" aria-busy={pending}>
    <img className="brand-mark large" src="/icon.svg" alt="" aria-hidden="true" />
    <h1 id="reauth-title">Подтверждение входа</h1>
    <p>Продолжите привязку почты к тому же аккаунту.</p>
    {!valid && <p role="alert" className="error">Исходное подтверждение не найдено или изменилось. Вернитесь к настройкам или исходному письму.</p>}
    {error && <p role="alert" className="error">{error}</p>}
    {waiting && <p role="status">Подтверждение ещё не завершено. Завершите вход в Яндекс ID и повторите проверку.</p>}
    {retryAt > 0 && <p role="status">Дождитесь окончания паузы.</p>}
    {valid && !terminal && <>
      <button className="button button-primary wide" disabled={pending || retryAt > 0} onClick={() => void finish()}>{pending ? 'Проверяем…' : 'Продолжить привязку'}</button>
      <button className="button button-secondary wide" disabled={pending || retryAt > 0} onClick={() => void finish(true)}>Отменить подтверждение</button>
    </>}
    <Link className="button button-secondary wide" to={SIGN_IN_METHODS}>К способам входа</Link>
  </section></main>;
}
